/*
  Pointer and tilt tracking for the ambient background.

  Strictly an enhancement: the light already breathes in CSS, so if this
  never runs the background is still alive. All this adds is the sense that
  the light belongs to the room the visitor is in.

  Nothing is drawn per frame. Input is throttled to one animation frame and
  written to two custom properties; CSS does the rest.
*/
class MelivraAmbience extends HTMLElement {
  connectedCallback() {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.reduced.matches) return;

    this.px = 0;
    this.py = 0;
    this.queued = false;

    this.onPointer = this.onPointer.bind(this);
    this.onTilt = this.onTilt.bind(this);
    this.flush = this.flush.bind(this);

    // Pointer only where there is one. On touch this never fires, which is
    // why tilt exists.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      window.addEventListener('pointermove', this.onPointer, { passive: true });
    }

    // iOS hands the sensor over only after an explicit gesture, and asking
    // for that permission on a page whose job is to collect an email is a
    // worse trade than going without. Where it is granted freely, we use it.
    if (
      window.DeviceOrientationEvent &&
      typeof window.DeviceOrientationEvent.requestPermission !== 'function'
    ) {
      window.addEventListener('deviceorientation', this.onTilt, { passive: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('pointermove', this.onPointer);
    window.removeEventListener('deviceorientation', this.onTilt);
  }

  onPointer(event) {
    this.px = (event.clientX / window.innerWidth - 0.5) * 2;
    this.py = (event.clientY / window.innerHeight - 0.5) * 2;
    this.schedule();
  }

  onTilt(event) {
    if (event.gamma === null || event.beta === null) return;
    // Beta is measured from flat, and a phone is usually read at a tilt, so
    // the resting position is offset rather than centred.
    this.px = clamp(event.gamma / 45, -1, 1);
    this.py = clamp((event.beta - 45) / 45, -1, 1);
    this.schedule();
  }

  schedule() {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(this.flush);
  }

  flush() {
    this.queued = false;
    this.style.setProperty('--mv-shift-x', (this.px * 3).toFixed(2) + '%');
    this.style.setProperty('--mv-shift-y', (this.py * 2).toFixed(2) + '%');
  }
}

function clamp(value, min, max) {
  return value < min ? min : value > max ? max : value;
}

if (!customElements.get('melivra-ambience')) {
  customElements.define('melivra-ambience', MelivraAmbience);
}
